import { PAGE_SLOT_COUNT } from "./slotCount";
// The pure rules of the drag-and-drop page slot reorder (docs/DRAG_DROP_SLOTS_SCOPE.md). No DOM, no network, never throws.
//
// A drag ends on a slot. This decides what that gesture MEANS, so the interaction code only has to ask "what happens if I drop here?"
// and the answer is testable without a browser.
//
//   - drop on an EMPTY slot           -> a MOVE
//   - drop on ANOTHER page's slot     -> a SWAP (the two exchange slots; nothing else shifts)
//   - drop on the homepage's slot, on "All other pages", outside the grid, or on the page's own slot -> nothing, with a reason
//
// The backend (SiteApi `reorderPages`, Helper/reorderMoves.ts) validates the same rules independently; this is the client's copy so
// the UI can show a not-allowed state while dragging instead of finding out from a failed request.

// The grid has 12 real page slots, 0..11; slot 12 is "All other pages". Slot 0 is the homepage's. Mirrors the backend's
// MAX_PAGE_SLOT_INDEX and clusterService's DEFAULT_VALUE: change all three together.
export const HOME_SLOT = 0;
export const MAX_PAGE_SLOT = PAGE_SLOT_COUNT - 1;

export interface SlotPage {
  name: string;
  selected?: boolean;
  slotIndex?: number | null;
  restricted?: boolean;
  disabled?: boolean;
}

// What the RPC sends: the page, where the client believes it is, and where it should go.
export interface SlotChange {
  pageName: string;
  fromSlot: number;
  toSlot: number;
}

export type RejectReason =
  | "no-page" // nothing is being dragged from that slot
  | "ambiguous" // two selected pages claim the source slot: refuse rather than guess
  | "home" // slot 0 is the homepage's: not draggable, not a target
  | "outside" // not a page slot (e.g. "All other pages" or off the grid)
  | "locked"; // a restricted or disabled page

export type MovePlan =
  | { kind: "none" } // dropped back where it started
  | { kind: "move"; changes: SlotChange[] }
  | { kind: "swap"; changes: SlotChange[] }
  | { kind: "reject"; reason: RejectReason };

const isPageSlot = (slot: unknown): slot is number =>
  typeof slot === "number" && Number.isInteger(slot) && slot >= HOME_SLOT && slot <= MAX_PAGE_SLOT;

const occupantsOf = (pages: SlotPage[], slot: number) =>
  pages.filter((page) => page?.selected && page.slotIndex === slot);

export const planMove = (pages: SlotPage[], fromSlot: number, toSlot: number): MovePlan => {
  if (!Array.isArray(pages) || !isPageSlot(fromSlot) || !isPageSlot(toSlot)) {
    return { kind: "reject", reason: "outside" };
  }

  if (fromSlot === HOME_SLOT || toSlot === HOME_SLOT) {
    return { kind: "reject", reason: "home" };
  }

  const dragged = occupantsOf(pages, fromSlot);

  if (dragged.length === 0) {
    return { kind: "reject", reason: "no-page" };
  }

  if (dragged.length > 1) {
    return { kind: "reject", reason: "ambiguous" };
  }

  if (fromSlot === toSlot) {
    return { kind: "none" };
  }

  const [page] = dragged;

  if (page.restricted || page.disabled) {
    return { kind: "reject", reason: "locked" };
  }

  const occupants = occupantsOf(pages, toSlot);

  if (occupants.length === 0) {
    return { kind: "move", changes: [{ pageName: page.name, fromSlot, toSlot }] };
  }

  if (occupants.length > 1) {
    return { kind: "reject", reason: "ambiguous" };
  }

  const [other] = occupants;

  if (other.restricted || other.disabled) {
    return { kind: "reject", reason: "locked" };
  }

  return {
    kind: "swap",
    changes: [
      { pageName: page.name, fromSlot, toSlot },
      { pageName: other.name, fromSlot: toSlot, toSlot: fromSlot },
    ],
  };
};

// Apply a plan to a copy of the pages (the caller keeps the original for a revert). Only the slots of the pages in `changes` differ.
export const applyChanges = <T extends SlotPage>(pages: T[], changes: SlotChange[]): T[] =>
  pages.map((page) => {
    const change = page?.selected ? changes.find((c) => c.pageName === page.name && c.fromSlot === page.slotIndex) : undefined;

    return change ? { ...page, slotIndex: change.toSlot } : page;
  });

// The inverse of a plan: what to apply to undo it. A move back, or the same swap.
export const invertChanges = (changes: SlotChange[]): SlotChange[] =>
  changes.map((c) => ({ pageName: c.pageName, fromSlot: c.toSlot, toSlot: c.fromSlot }));

// Customer-facing wording for a refused drop. Plain statements, no blame.
export const rejectMessage = (reason: RejectReason): string => {
  switch (reason) {
    case "home":
      return "The homepage stays in the first slot.";
    case "outside":
      return "Pages can only be placed in the page slots.";
    case "locked":
      return "That page is locked on your current plan.";
    case "ambiguous":
    case "no-page":
    default:
      return "That move could not be made.";
  }
};
